import { cloneDeep } from 'lodash-es';
import { UseFormReturn } from 'react-hook-form';
import * as yup from 'yup';
import { buildFormatterFormField } from '../../../components/FormatterSelect/FormatterSelect';
import { formatterModelsByTypeAtom } from '../../../components/FormatterSelect/formatter-options-state';
import { RendererType } from '../../../data-models/field-definition';
import { ISimpleChoice } from '../../../data-models/field2.data-model';
import { getFormatterForType, ISelectMeta, PrimitiveType } from '../../../data-models/field3.data-model';
import { getForesightStore } from '../../../util/jotai-store';
import { schemaToFormFields } from '../../../util/schema-utils';
import { IForm } from '../../../view-models/form.view-model';
import { ColumnMeta, ColumnType, createColumnMeta } from '../../../types';

const COLUMN_TYPES: ColumnType[] = ['boolean', 'string', 'link', 'date', 'number', 'stringArray'];

const TYPE_OPTIONS: ISimpleChoice<ColumnType>[] = COLUMN_TYPES.map((value) => ({
  value,
  displayName: value,
}));

/** The formatter family each `ColumnType` draws its Format options from. */
const COLUMN_TYPE_TO_PRIMITIVE: Record<ColumnType, PrimitiveType> = {
  boolean: 'boolean',
  string: 'string',
  link: 'string',
  date: 'date',
  number: 'number',
  stringArray: 'array',
};

function booleanField(label: string) {
  return yup.boolean().label(label).formMeta({ renderer: RendererType.boolean });
}

function numberField(label: string) {
  return yup
    .number()
    .label(label)
    .formMeta({ renderer: RendererType.number, rendererMeta: { groupingSeparator: '' } });
}

function textField(label: string) {
  return yup
    .string()
    .label(label)
    .formMeta({ renderer: RendererType.text, rendererMeta: { maxLength: -1 } });
}

/**
 * Column config schema bound directly to `ColumnMeta` (keys are ColumnMeta field
 * names). Exposes all 21 configurable fields; the `meta` sub-object and the
 * system identity/audit fields (`id`, `createdAt/By`, `updatedAt/By`) are not
 * edited — they ride along untouched on the form's data snapshot at save time.
 */
export function columnConfigFormSchema() {
  return yup.object({
    name: yup
      .string()
      .required()
      .label('Key')
      .formMeta({
        renderer: RendererType.text,
        description:
          'Unique identifier — the saved-view colId. Locked after creation; renaming would break saved views.',
        rendererMeta: { maxLength: -1 },
      }),
    path: textField('Path'),
    displayName: yup
      .string()
      .required()
      .label('Display Name')
      .formMeta({ renderer: RendererType.text, rendererMeta: { maxLength: -1 } }),
    description: textField('Description'),
    type: yup
      .string()
      .required()
      .label('Type')
      .formMeta({
        renderer: RendererType.singleSelect,
        rendererMeta: {
          multi: false,
          values: TYPE_OPTIONS,
          formatValueToLabel: true,
        } as ISelectMeta<ColumnType>,
      }),
    // Options are filled per-Type by `columnFormTransformer`; declared here only to
    // hold the field's place in the form order.
    formatting: yup.string().label('Format').formMeta({ renderer: RendererType.singleSelect }),
    tableOrder: numberField('Sort Order'),
    groupName: textField('Group Name'),
    groupOrder: numberField('Group Order'),
    groupPriority: numberField('Group Priority'),
    visible: booleanField('Visible'),
    default: booleanField('Default'),
    required: booleanField('Required'),
    editable: booleanField('Editable'),
    exportable: booleanField('Exportable'),
    filterable: booleanField('Filterable'),
    sortable: booleanField('Sortable'),
    groupable: booleanField('Groupable'),
    aggregation: booleanField('Aggregation'),
    aggFunc: textField('Aggregation Function'),
    options: textField('Options'),
  });
}

export function getColumnFields() {
  return schemaToFormFields(columnConfigFormSchema(), [
    'name',
    'path',
    'displayName',
    'description',
    'type',
    'formatting',
    'tableOrder',
    'groupName',
    'groupOrder',
    'groupPriority',
    'visible',
    'default',
    'required',
    'editable',
    'exportable',
    'filterable',
    'sortable',
    'groupable',
    'aggregation',
    'aggFunc',
    'options',
  ]);
}

/**
 * Finalize the form value before submit. `createColumnMeta` fills any field the
 * form didn't render (and preserves `meta` + audit fields carried on the row),
 * then we mirror `path` from `name` for new rows and keep nullable fields null
 * rather than empty.
 */
export function transformColumnBeforeSubmit(form: ColumnMeta): ColumnMeta {
  const base = createColumnMeta(form);
  return {
    ...base,
    path: base.path || base.name,
    tableOrder: base.tableOrder ?? null,
    aggFunc: base.aggFunc || null,
  };
}

/**
 * Dynamic form rules:
 * - Locks `name` and `path` on edit (non-zero `id`): `name` is the persisted
 *   saved-view colId (FD-2794 contract) and `path` is the data binding —
 *   renaming either breaks existing saved views / data display.
 * - Fills `formatting` with the shared formatter picker, scoped to the family the
 *   currently-selected `type` can use — the same control the KPI and custom-field
 *   forms show, so a Format reads the same wherever it is set.
 */
export function columnFormTransformer(
  form: IForm<ColumnMeta>,
  methods: UseFormReturn<ColumnMeta>
): IForm<ColumnMeta> {
  const dynamicForm = cloneDeep(form);
  const id = (methods.watch('id') ?? form.data?.id ?? 0) as number;
  const isEdit = id !== 0;

  for (const key of ['name', 'path'] as const) {
    const field = dynamicForm.fields.get(key);
    if (field) {
      dynamicForm.fields.set(key, { ...field, disabled: isEdit });
    }
  }

  const type = (methods.watch('type') ?? form.data?.type) as ColumnType | undefined;
  const primitive = type ? COLUMN_TYPE_TO_PRIMITIVE[type] : undefined;
  // Fill the slot the schema declared; never invent one the form did not ask for.
  if (dynamicForm.fields.has('formatting') && primitive) {
    const current = (methods.watch('formatting') ?? form.data?.formatting) as string | undefined;
    const picker = buildFormatterFormField({
      formatterModelsByType: getForesightStore().get(formatterModelsByTypeAtom),
      key: 'formatting',
      label: 'Format',
      type: primitive,
      value: current,
    });
    if (picker) {
      const choices = (picker.rendererMeta as ISelectMeta<string> | undefined)?.values ?? [];
      // A type switch — or a legacy id the registry no longer offers — leaves a formatter
      // that cannot apply. Snap to the type's default rather than render a blank input.
      if (current && !choices.some((choice) => choice.value === current)) {
        methods.setValue('formatting', getFormatterForType(primitive).id);
      }
      dynamicForm.fields.set('formatting', picker);
    }
  }

  return dynamicForm;
}
